import {useLocation, useRouteMatch} from 'react-router-dom';

import {
    getMaxLengthMessage,
    getMinLengthMessage,
    getRegexMessage,
    getRequiredMessage,
    getUppercaseMessage,
    getValidationError,
} from './Messagefunctions';
import {
    Child,
    Documents,
    EfeCatalog,
    EfeResponse,
    EfeViewsArray,
    Field,
    Layout,
    Validation,
} from 'commons/models';
import {
    ActivePathType,
    GeolocationInfo,
    KeycloakTokenResponse,
    ModuleItem,
} from 'commons/types/Types';
import {
    ADMIN_ID_SALESFORCE,
    Constants,
    DOB_ATTR,
    Messages,
    MIN_AGE,
    Regex,
    TypeElement,
    USER_DATA,
    Validations,
} from 'commons/constants';
import {KeycloakService} from 'services';
import {getWhitelistedRegex, isWhitelistedPattern} from './RegexUtils';

/**
 * Get an object property, using object keys
 * @param {Type} obj object to access
 * @param {Key} key property key
 * @returns {Type[Key]} Inferred type & key
 */
export function getProperty<Type, Key extends keyof Type>(obj: Type, key: Key) {
    return obj[key];
}

/**
 * Not null or undefined validation
 * @param {TValue | null | undefined} value
 * @returns {value is not empty}
 */
export function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
    return value !== null && value !== undefined;
}

export async function sleep(delay = 0) {
    return new Promise((resolve) => {
        setTimeout(resolve, delay * 100);
    });
}

/**
 * Generate a random seed
 * @returns {string}
 */
const seed = function () {
    const rnd = function () {
        const arr = new Uint32Array(1);
        window.crypto.getRandomValues(arr);
        return arr[0].toString(36);
    };
    return Date.now().toString(36) + rnd();
};

/**
 * Generate a unique id with prefix
 * @param {string} postfix
 * @returns {string}
 */
const uuid = function (postfix: string | number) {
    return `efe-${seed()}-${postfix}`;
};

export const Generate = {
    uniqueID: (postfix?: string | number) => uuid(postfix || seed()),
};

export const downloadDocs = (response: EfeResponse<Documents>, filename: string) => {
    const blob = new Blob([response.data?.data], {
        type: response.data?.data.type,
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', response.data ? response.data.filename : filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

/**
 * Sort Any Array using <code>key</code>
 * @param {Array<T>} array
 * @param {keyof T} key
 * @param asc
 * @returns {Array<T>}
 */
export function sort<T>(array: Array<T>, key: keyof T, asc?: boolean) {
    const minToMax = asc || true;
    if (array.length <= 1) {
        return array;
    }
    const predicate = (a: T, b: T) => {
        const aName = getProperty(a, key);
        const bName = getProperty(b, key);
        const leftMin = aName < bName;
        const rightMin = bName < aName;
        if (leftMin) {
            return minToMax ? -1 : 1;
        }
        if (rightMin) {
            return minToMax ? 1 : -1;
        }
        return 0;
    };
    return array.sort(predicate);
}

export function modulesValidation(
    routes: Array<ModuleItem>,
    userModules: Layout
): Array<ModuleItem> {
    const tempRoutes: Array<ModuleItem> = [];
    if (userModules) {
        userModules.views.map((item) => {
            const tempModule: ModuleItem = Object.assign(
                {},
                routes.find((route) => route.id === item.viewKey)
            );
            if (tempModule && notEmpty(tempModule.path)) {
                if (tempModule.items && tempModule.items.length > 0) {
                    tempModule.items = [...subModulesValidation(tempModule.items, item.children)];
                }
                tempRoutes.push({
                    ...{...tempModule},
                    viewId: item.viewId,
                    fatherId: item.viewFatherId,
                    linkedChildId: item.linkViewChildId,
                    linkedFatherId: item.linkViewFatherId,
                });
            }
        });
    }
    return tempRoutes.sort((a: ModuleItem, b: ModuleItem) => (a.orderId < b.orderId ? -1 : 1));
}

function subModulesValidation(
    childs: Array<ModuleItem>,
    subModules: Array<Child>
): Array<ModuleItem> {
    const tempSubModules: Array<ModuleItem> = [];
    childs.forEach((child) => {
        if (child.thirdLevel && child.items) {
            child.items = [...subModulesValidation(child.items, subModules)];
            tempSubModules.push({...child});
        } else {
            const tempSubModule: Child = Object.assign(
                {},
                subModules.find((subModule) => child.id === subModule.viewKey)
            );
            if (tempSubModule && tempSubModule.viewKey && tempSubModule.viewKey.length) {
                tempSubModules.push({
                    ...child,
                    viewId: tempSubModule.viewId,
                    fatherId: tempSubModule.viewFatherId,
                    linkedChildId: tempSubModule.linkViewChildId,
                    linkedFatherId: tempSubModule.linkViewFatherId,
                });
            }
        }
    });
    return tempSubModules.sort((a: ModuleItem, b: ModuleItem) => (a.orderId < b.orderId ? -1 : 1));
}

function validateField(field: Field, validations: Validation[], errors: any, values: any) {
    if (values[field.attrName] && field.validations && field.validations.length) {
        for (const validation of validations) {
            let message = '';
            switch (validation.typeValidation) {
                case Validations.MIN_LENGTH: {
                    message = getMinLengthErrorMessage(validation, values[field.attrName], field);
                    break;
                }
                case Validations.MAX_LENGTH: {
                    message = getMaxLengthErrorMessage(validation, values[field.attrName], field);
                    break;
                }
                case Validations.REGEX: {
                    message = getRegexErrorMessage(validation, values[field.attrName], field);
                    break;
                }
                case Validations.UPPERCASE: {
                    message = getUppercaseErrorMessage(validation, values[field.attrName], field);
                    break;
                }
            }
            if (message && notEmpty(message)) {
                errors[field.attrName] = message;
            }
        }
    } else if (!values[field.attrName]) {
        if (field.required) {
            errors[field.attrName] = getRequiredMessage(field.label);
        }
    }
}

function datePickerFieldValidation(field: Field, errors: any, values: any) {
    if (isFutureDate(values[field.attrName])) {
        errors[field.attrName] = Messages.INVALID_FUTURE_DATE;
    }
    if (DOB_ATTR === field.attrName && getAge(values[field.attrName]) < MIN_AGE) {
        errors[field.attrName] = Messages.INVALID_DATE_OF_BIRTH;
    }
}

export function formValidator(fields: Array<Field>, errors: any, values: any) {
    const safeValues: Record<string, any> = Object.create(null);
    Object.keys(values).forEach((key) => {
        safeValues[key] = values[key];
    });
    if (fields && fields.length) {
        for (const field of fields) {
            validateField(field, field.validations, errors, safeValues);
            if (TypeElement.DATEPICKER === field.typeElement && values[field.attrName]) {
                datePickerFieldValidation(field, errors, safeValues);
            }
        }
    }
}

function getUppercaseErrorMessage(validation: Validation, value: any, field: Field) {
    if (!Regex.UPPERCASE.test(value)) {
        return validation.errorMessage ? validation.errorMessage : getUppercaseMessage(field.label);
    }
    return '';
}

function getRegexErrorMessage(validation: Validation, value: any, field: Field) {
    if (!validation.value) {
        console.error('Error de configuracion');
        return getValidationError(field.label);
    }

    const input = String(value ?? '');

    if (!isWhitelistedPattern(validation.value)) {
        console.error('Patrón NO permitido por whitelist:', validation.value, '');
        return '';
    }
    const regex = getWhitelistedRegex(validation.value);
    if (!regex) {
        console.error('No se pudo obtener el regex de la whitelist (esto no debería pasar).');
        return 'Error en configuración de validación';
    }
    if (!regex.test(input)) {
        if (validation.errorMessage) {
            return validation.errorMessage;
        } else {
            return getRegexMessage(field.label);
        }
    }
    return '';
}

function getMaxLengthErrorMessage(validation: Validation, value: any, field: Field) {
    const length = Number(validation.value);
    if (!length || isNaN(length)) {
        console.error('Error de configuracion');
        return getValidationError(field.label);
    }
    if (value.length > length) {
        if (validation.errorMessage) {
            return validation.errorMessage;
        } else {
            return getMaxLengthMessage(field.label, length);
        }
    }
    return '';
}

function getMinLengthErrorMessage(validation: Validation, value: any, field: Field) {
    const length = Number(validation.value);
    if (!length || isNaN(length)) {
        console.error('Error de configuracion');
        return getValidationError(field.label);
    }
    if (value.length < length) {
        if (validation.errorMessage) {
            return validation.errorMessage;
        } else {
            return getMinLengthMessage(field.label, length);
        }
    }
    return '';
}

function thirdLevelStep(flow: ActivePathType, items: Array<ModuleItem>, ref: number): string {
    let path = '';
    let tempItems: Array<ModuleItem> = [];
    if (items.length > 1 && items.length > ref) {
        tempItems = items[ref].items || [];
    } else if (items.length > 1 && items.length < ref) {
        tempItems = items[items.length - 1].items || [];
    } else if (items.length === 1 && items[0].thirdLevel && items[0].items) {
        tempItems = items[0].items;
    } else {
        console.error('error ubicando siguiente paso, link roto (thirdLevel)');
    }
    const child: ModuleItem | undefined = tempItems.find(
        (submodule) => submodule.viewId === flow.data.linkedChildId
    );
    if (child) {
        path = `${child.layout}${child.path}`;
    } else {
        console.error('error ubicando siguiente paso, link roto (thirdLevel, items)');
    }
    return path;
}

export function nextStep(
    flow: ActivePathType,
    menu: ModuleItem[],
    exclude?: boolean,
    ref?: number
): string {
    let path = '';
    if (flow.data.linkedFatherId !== flow.data.fatherId && !exclude) {
        flow.data.moduleStep = true;
        path = Constants.MODULE_STEP;
    } else {
        const item: ModuleItem | undefined = menu.find(
            (module) => module.viewId === flow.data.linkedFatherId
        );
        if (item) {
            if (flow.data.linkedChildId && item.items) {
                const child: ModuleItem | undefined = item.items.find(
                    (submodule) => submodule.viewId === flow.data.linkedChildId
                );
                if (child) {
                    path = `${child.layout}${child.path}`;
                } else {
                    path = thirdLevelStep(flow, item.items, ref || 0);
                }
            } else {
                path = `${item.layout}${item.path}`;
            }
        } else {
            console.error('error ubicando siguiente paso, link roto');
        }
    }
    if (!menu || menu.length <= 0) {
        console.error('Error ubicando referencia');
    }
    return path;
}

export function useViewSearch(flow: ActivePathType, menu: Array<ModuleItem>): Array<EfeViewsArray> {
    const views: Array<EfeViewsArray> = [];
    const module: ModuleItem | undefined = menu.find((item) => item.viewId === flow.data.fatherId);
    if (module && module.items) {
        module.items.map((view: ModuleItem, index: number) => {
            if (view.thirdLevel && view.items) {
                view.items.map((nestedView: ModuleItem, key: number) => {
                    views.push({
                        view: nestedView.view,
                        index: `${view.id}-${index}-${key}`,
                    });
                });
            } else {
                views.push({
                    view: view.view,
                    index: `${view.id}-${index}`,
                });
            }
        });
    } else if (module) {
        views.push({
            view: module.view,
            index: module.id,
        });
    }
    return views;
}

export function formatDateToISO(date: Date | undefined | null | string): string | null {
    if (date) {
        const formated = new Date(date);
        return formated.toISOString().split('T')[0];
    }
    return null;
}

export function getBase64Extension(base64: string) {
    return base64.substring(base64.indexOf('/') + 1, base64.indexOf(';base64'));
}

function findActiveChild(
    items: Array<ModuleItem>,
    matchPath: string,
    currentPath: string
): Array<ModuleItem> {
    let filtered;
    for (const item of items) {
        if (item.thirdLevel) {
            if (item.items) {
                filtered = item.items.filter((i) => `${matchPath}${i.path}` === currentPath);
            }
            if (filtered && filtered.length) {
                break;
            }
        } else {
            filtered = items.filter((i) => `${matchPath}${i.path}` === currentPath);
            break;
        }
    }
    return filtered || [];
}

export function findStepIndex(menuItems: ModuleItem[], item: ModuleItem): number {
    return menuItems.filter((menu: ModuleItem) => menu.showLinearProgress).indexOf(item);
}

export function findActivePath(menuItems: ModuleItem[]): ActivePathType {
    const location = useLocation();
    const match = useRouteMatch();
    const currentPath = location.pathname;
    const matchPath = match.path;
    const firstPage = menuItems[0];
    let activePath: ActivePathType;
    let filtered = menuItems.filter((i) => `${matchPath}${i.path}` === currentPath);
    let stepIndex = 0;
    if (!filtered || !filtered.length) {
        for (const item of menuItems) {
            if (item.items) {
                filtered = findActiveChild(item.items, matchPath, currentPath);
            }
            if (filtered && filtered.length) {
                stepIndex = findStepIndex([...menuItems], item);
                break;
            }
        }
    } else {
        stepIndex = findStepIndex([...menuItems], filtered[0]);
    }
    if (filtered.length >= 1) {
        activePath = {
            data: {
                ...filtered[0],
                index: stepIndex,
                moduleStep: false,
            },
        };
    } else {
        activePath = {
            data: {
                ...firstPage,
                index: stepIndex,
                moduleStep: false,
            },
        };
    }
    return activePath;
}

export async function refreshToken() {
    try {
        const token: EfeResponse<KeycloakTokenResponse> = await KeycloakService.refreshToken(
            USER_DATA
        );
        if (token && token.data && token.data.access_token) {
            USER_DATA.loggedIn = true;
            USER_DATA.tokenType = token.data.token_type;
            USER_DATA.token = token.data.access_token;
            USER_DATA.expires = token.data.expires_in;
            USER_DATA.refreshToken = token.data.refresh_token;
        }
    } catch (error) {
        USER_DATA.logout = true;
        USER_DATA.loggedIn = false;
        console.log('refreshDataError: ', error);
    }
}

export function onTime(expirationTime: Date): boolean {
    return new Date().getTime() < expirationTime.getTime();
}

export function getModalStyle(width: number) {
    return {
        width: `${width}%`,
        top: '0%',
        left: '0%',
        transform: 'translate(0%, 0%)',
    };
}

export function validateDateonRFCandCURP(date: string | Date, identifier: string): boolean {
    try {
        const dateOfIdentifier = identifier.substring(4, 10);
        const auxDate = new Date(date);
        const partDate = auxDate.toISOString().substring(2, 10).replaceAll('-', '');
        if (dateOfIdentifier === partDate) {
            return true;
        }
        return false;
    } catch (error) {
        return false;
    }
}

export function validateDateonMoralRFC(date: string | Date, identifier: string): boolean {
    try {
        const dateOfIdentifier = identifier.substring(3, 9);
        const auxDate = new Date(date);
        const partDate = auxDate.toISOString().substring(2, 10).replaceAll('-', '');
        if (dateOfIdentifier === partDate) {
            return true;
        }
        return false;
    } catch (error) {
        return false;
    }
}

export function isFutureDate(date: string | Date): boolean {
    if (!date) {
        return false;
    } else {
        const auxDate = new Date(date);
        return auxDate > new Date();
    }
}

export function standardDateToObject(date: string): Date | null {
    if (Regex.STANDARD_DATE_FORMAT.test(date)) {
        const dateParts: string[] = date.split('/');
        return new Date(`${dateParts[1]}/${dateParts[0]}/${dateParts[2]}`);
    }
    return null;
}

export function useFieldCatalogGetter(
    fields: Array<Field>,
    catalogs: Array<EfeCatalog>
): Array<Field> {
    const filteredFields: Array<Field> = fields.filter(
        (field) => Constants.ATTR_NULL !== field.attrName
    );
    const customFields: Array<Field> = [];
    for (const field of filteredFields) {
        const customField: Field = {...field, catalogValues: []};
        if (TypeElement.SELECT === customField.typeElement) {
            if (notEmpty(customField.catalog)) {
                const catalog: EfeCatalog | undefined = catalogs.find(
                    (item: EfeCatalog) => item.name === customField.catalog
                );
                if (catalog) {
                    customField.catalogValues = [...catalog.values];
                } else {
                    customField.catalogValues = [];
                    console.error(`unknown catalog for ${customField.label}`);
                }
            } else {
                customField.catalogValues = [];
                console.error(`unknown catalog for ${customField.label}`);
            }
        }
        customFields.push(customField);
    }
    return customFields.sort((a: Field, b: Field) => (a.idOrder < b.idOrder ? -1 : 1));
}

export function resetUserDataConstant() {
    USER_DATA.username = '';
    USER_DATA.idSalesforce = '';
    USER_DATA.email = '';
    USER_DATA.fullName = '';
    USER_DATA.loggedIn = false;
    USER_DATA.tokenType = '';
    USER_DATA.token = '';
    USER_DATA.expires = 0;
    USER_DATA.refreshToken = '';
    USER_DATA.pendingActions = [''];
    USER_DATA.expirationDate = null;
}

export function isCustomErrorService(error: any): string {
    return error && error.data && error.data.process && error.data.message && error.data.errors;
}

export function clearUnusedFields<T>(values: any, fields: Array<Field>): T {
    const cleanValues: {[k: string]: any} = {};
    fields.forEach((field) => {
        cleanValues[field.attrName] = values[field.attrName];
    });
    return cleanValues as T;
}

export function setDefaultValue(value: any, defaultValue: any): any {
    return value ? value : defaultValue;
}

export function getAge(birthdate: Date | string): number {
    const today = new Date();
    const dob = new Date(birthdate);
    let age = today.getFullYear() - dob.getFullYear();
    const month = today.getMonth() - dob.getMonth();
    if (month < 0 || (month === 0 && today.getDate() < dob.getDate())) {
        age--;
    }
    return age;
}

export async function getGeolocation(): Promise<GeolocationInfo> {
    let data: GeolocationInfo = {
        latitude: null,
        longitude: null,
    };
    try {
        const geolocation: any = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject);
        });

        data = {
            latitude: geolocation.coords.latitude,
            longitude: geolocation.coords.longitude,
        };
    } catch (error) {
        console.error(error);
    }
    return data;
}

export function isAdmin(idSalesforce: string): boolean {
    return ADMIN_ID_SALESFORCE === idSalesforce;
}

export function blobToBase64(blob: any): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            const base64String = reader.result;
            resolve(base64String?.toString() || '');
        };

        reader.onerror = () => {
            reject(new Error('Error reading the Blob.'));
        };

        reader.readAsDataURL(blob);
    });
}
export const getRandomHex = function () {
    const bytes = new Uint8Array(8);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
};

export async function convertBase64ToBlob(dataUrl: string) {
    if (!dataUrl.startsWith('data:')) {
        throw new Error('Formato no soportado');
    }

    // Extraer metadata
    const matches = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
    if (!matches) {
        throw new Error('Formato base64 inválido');
    }

    const mimeType = matches[1];
    const base64Data = matches[2];

    const allowedMimeTypes = [
        'image/jpeg',
        'image/png',
        'image/jpg',
        'image/gif',
        'application/pdf',
    ];

    if (!allowedMimeTypes.includes(mimeType)) {
        throw new Error(`Tipo de archivo no permitido: ${mimeType}`);
    }

    const byteCharacters = atob(base64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, {type: mimeType});
}
